import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { StatusBadge, NationBadge } from "@/components/Compliance";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import UpgradeDialog from "@/components/UpgradeDialog";

export default function Properties() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", postcode: "", nation: user?.nations?.[0] || "England", property_type: "single" });

  const load = () => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/billing/usage").then((r) => setUsage(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    if (usage && usage.used >= usage.limit) return setUpgrade(usage);
    setOpen(true);
  };

  const create = async () => {
    if (!form.name || !form.address) return toast.error("Fill in name and address");
    try { await api.post("/properties", form); toast.success("Property added"); setOpen(false); setForm({ ...form, name: "", address: "", postcode: "" }); load(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d?.code === "plan_limit") { setOpen(false); setUpgrade(d); return; }
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Properties</h1>
          {usage && <p className="text-xs text-muted-foreground mt-1" data-testid="plan-usage"><span className="capitalize">{usage.plan}</span> plan · {usage.used} of {usage.limit} properties used</p>}
        </div>
        <button data-testid="new-property-btn" onClick={openAdd} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold transition-colors"><Plus size={18} /> Add</button>
      </div>
      <UpgradeDialog info={upgrade} onClose={() => setUpgrade(null)} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {props.map((p) => (
          <Link key={p.id} to={`/properties/${p.id}`} data-testid={`property-${p.id}`} className="bg-card border border-border rounded-2xl p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 grid place-items-center rounded-lg bg-secondary"><Building2 size={18} className="text-navy" /></div>
              <NationBadge nation={p.nation} />
            </div>
            <div className="mt-3 font-semibold">{p.name}</div>
            <div className="text-sm text-muted-foreground truncate">{p.address}</div>
            <div className="mt-2 text-xs text-muted-foreground capitalize">{p.property_type === "hmo" ? "HMO" : "Single let"}</div>
            <div className="mt-4 flex items-center justify-between">
              <StatusBadge status={p.status} />
              <span className="font-heading font-extrabold text-lg">{p.score}<span className="text-xs text-muted-foreground">/100</span></span>
            </div>
          </Link>
        ))}
        {props.length === 0 && <div className="text-sm text-muted-foreground">No properties yet — add your first one.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="new-property-modal">
          <DialogHeader><DialogTitle className="font-heading">Add a property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="np-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Property name / nickname" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            <input data-testid="np-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            <input data-testid="np-postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            <select data-testid="np-nation" value={form.nation} onChange={(e) => setForm({ ...form, nation: e.target.value })} className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card">
              {(user?.nations?.length ? user.nations : ["England", "Wales", "Scotland"]).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              {[["single", "Single let"], ["hmo", "HMO"]].map(([v, l]) => (
                <button key={v} data-testid={`np-type-${v}`} onClick={() => setForm({ ...form, property_type: v })} className={`min-h-[44px] rounded-xl border-2 font-medium transition-colors ${form.property_type === v ? "border-emerald-brand bg-emerald-50/60" : "border-border"}`}>{l}</button>
              ))}
            </div>
            <button data-testid="np-save" onClick={create} className="w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors">Add property</button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
